import { ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import Button from '@superfunnel/ui-core/v2/Button';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism.css';
import { useState } from 'react';
import ReactCodeEditor from 'react-simple-code-editor';
import CodeEditorModal from '../../../modals/CodeEditorModal';
import { classNames } from '../../../utils';

interface Props {
  title?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  allowModal?: boolean;
  validation?: {
    status?: 'error' | 'success';
    text?: string;
  };
  onFocus?: () => void;
  onBlur?: () => void;
}

export const CodeEditorTextArea = ({
  title,
  value,
  onChange,
  disabled,
  className,
  allowModal,
  validation,
  onFocus,
  onBlur,
}: Props) => {
  const [codeModalOpen, setCodeModalOpen] = useState(false);

  return (
    <div className={classNames(className, 'flex flex-col')}>
      {(!!title || !!allowModal) && (
        <div className="relative">
          {!!title && <FieldTitle title={title} />}

          {allowModal && (
            <CodeEditorModal
              code={value ?? ''}
              onSave={onChange}
              open={codeModalOpen}
              onClose={() => {
                setCodeModalOpen(false);
              }}
            />
          )}

          {allowModal && !disabled && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="absolute end-0 -top-0.5"
              onClick={() => setCodeModalOpen(true)}
            >
              <ArrowsPointingOutIcon className="h-4 w-4 text-gray-500" />
            </Button>
          )}
        </div>
      )}

      <div className="contents flex-1 pt-1.5">
        <div
          className={classNames(
            'h-full overflow-scroll',
            'relative block w-full rounded-md border-0 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 focus:ring-inset sm:text-sm sm:leading-6',
          )}
        >
          <ReactCodeEditor
            value={value ?? ''}
            onValueChange={(code) => onChange(code)}
            highlight={(code) => highlight(code, languages.js, 'js')}
            padding={10}
            style={{
              fontFamily: '"Fira code", "Fira Mono", monospace',
              fontSize: 12,
            }}
            disabled={disabled}
            textareaClassName="min-h-full"
            className="min-h-full"
            onFocus={onFocus}
            onBlur={onBlur}
          />
        </div>
      </div>
      {validation?.text ? (
        <p
          className={classNames(
            'mt-2 text-sm',
            validation?.status === 'error' ? 'text-red-600' : '',
            validation?.status === 'success' ? 'text-green-600' : '',
          )}
          id="email-error"
        >
          {validation.text}
        </p>
      ) : (
        <></>
      )}
    </div>
  );
};
